import { useTranslation } from "react-i18next";
import { useAuth } from "@/models/useAuth";
import { config, desktop, useAppStore } from "@/controller";
import toast from "react-hot-toast";
import {
  ANDROID_APP_LINK,
  APP_DOWNLOAD_URLS,
  IOS_APP_LINK,
  contactUs,
  gotoPrivacy,
  gotoTerms,
  openEmailDraft,
} from "@/product-links";
import { accountEmail } from "@/auth-identity";
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { appLanguages, i18n } from "@/locales";
import { useRouter } from "@/_router";
import {
  CrownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  SunIcon,
  MoonIcon,
  SparklesIcon,
  TranslateIcon,
  ShareIcon,
  LightbulbIcon,
  CloseIcon,
  SystemIcon,
  LegalIcon,
  CardIcon,
  MicrophoneIcon,
  AppleIcon,
  AndroidIcon,
  WindowsIcon,
  DownloadIcon,
  FileTextIcon,
  TrashIcon,
} from "@/components/Icons";
import type { Transcript } from "@/types";
import { useTheme } from "@/models/useTheme";
import { ColorScheme, useUISlice } from "@/models/useUISlice";
import { runtime } from "@/controller";
import Container from "@/components/Container";
import { shareApp } from "@/models/useNative";
import { logClientEvent } from "@/telemetry";
import { actions } from "@/controller";
import CancelSubscriptionForm from "@/components/CancelSubscriptionForm";
import GlobalDictationOnboarding from "@/components/GlobalDictationOnboarding";
import { getPlatform } from "@/platform";
import {
  getBillingPeriodFromIdentifier,
  getPackageBillingPeriod,
} from "@/models/paywallMath";
import DictationVocabularyEditor from "@/components/DictationVocabularyEditor";
import DictationHistoryList from "@/components/DictationHistoryList";
import {
  readWebDictationHistory,
  type WebDictationHistoryItem,
} from "@/web-dictation-interactions";
import DesktopDictationSettings from "@/components/DesktopDictationSettings";
import { useSharedDesktopHost } from "@/models/desktop-host-context";
import { getNextEnabledValue } from "@/dictation-interactions";
import {
  PARAGRAPH_SENTENCE_LIMITS,
  type AgentConnectionSummary,
  type ParagraphSentenceLimit,
  type SubscriptionOffer,
} from "@/types";
import { LANGUAGES } from "@/languages";
import {
  configureGlobalDictationAsync,
  getGlobalDictationStatusAsync,
  getRecentGlobalDictationsAsync,
  isGlobalDictationSetupComplete,
  setGlobalDictationBubbleFaintAsync,
  setGlobalDictationEnabledAsync,
  type GlobalDictationStatus,
} from "@/models/useGlobalDictation";

// Settings Button Component
const SettingsButton = ({
  text,
  subtext,
  onClick,
  actionId,
  destructive = false,
  icon,
  expanded,
  controls,
  singleLine = false,
  disabled = false,
}: {
  text: string;
  subtext?: string;
  onClick: () => void;
  actionId?: string;
  destructive?: boolean;
  icon?: React.ReactNode;
  expanded?: boolean;
  controls?: string;
  singleLine?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    data-action-id={actionId}
    aria-expanded={expanded}
    aria-controls={controls}
    className={`group relative flex min-h-[76px] w-full items-center justify-between overflow-hidden px-5 py-4 text-start transition-[background-color,transform] duration-200 hover:bg-[var(--glass-bg-hover)] active:scale-[0.995] active:bg-[var(--glass-bg-active)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--accent)] ${
      destructive
        ? "bg-[var(--destructive-bg)] text-[var(--destructive)]"
        : "text-[var(--text-primary)]"
    }`}
  >
    <div className="flex min-w-0 items-center gap-4">
      {icon && (
        <div
          aria-hidden="true"
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
            destructive
              ? "bg-[var(--destructive-bg)] text-[var(--destructive)]"
              : "bg-[var(--fill-1)] group-hover:bg-[var(--fill-2)] text-[var(--text-primary)]"
          }`}
        >
          {icon}
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0">
        <span
          className={`${singleLine ? "truncate" : "break-words"} text-[16px] font-semibold tracking-tight`}
        >
          {text}
        </span>
        {subtext && (
          <span
            className={`break-words text-[13px] font-medium leading-snug ${
              destructive
                ? "text-[var(--destructive)]"
                : "text-[var(--text-secondary)]"
            }`}
          >
            {subtext}
          </span>
        )}
      </div>
    </div>
    {!destructive && (
      <div
        aria-hidden="true"
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--text-tertiary)] transition-[transform,color] duration-200 group-hover:text-[var(--text-secondary)] ${
          expanded ? "rotate-90" : ""
        }`}
      >
        <ChevronRightIcon size={20} className="text-[var(--text-secondary)]" />
      </div>
    )}
  </button>
);

// Deleted transcripts wait here for 7 days before the server erases them.
// ponytail: shows the first page (50); add "load more" if users keep more.
const RecentlyDeletedRow = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<Transcript[]>();
  const [busyId, setBusyId] = useState<string>();

  const toggle = async () => {
    const next = !isOpen;
    setIsOpen(next);
    if (!next) return;
    setItems(undefined);
    const result = await actions.transcript.listDeleted();
    if (!result.ok) {
      toast.error(t("settings.recentlyDeleted.loadFailed"));
      setItems([]);
      return;
    }
    setItems(result.value.transcripts);
  };

  const run = async (id: string, operation: "restore" | "deleteForever") => {
    setBusyId(id);
    const result = await actions.transcript[operation](id);
    setBusyId(undefined);
    if (!result.ok) {
      toast.error(t("settings.recentlyDeleted.actionFailed"));
      return;
    }
    setItems((current) => current?.filter((item) => item.id !== id));
    toast.success(
      t(
        operation === "restore"
          ? "settings.recentlyDeleted.restored"
          : "settings.recentlyDeleted.erased",
      ),
    );
  };

  const confirmDeleteNow = (id: string) => {
    useUISlice.getState().setAlertModal({
      title: t("settings.recentlyDeleted.deleteNowTitle"),
      subtitle: t("settings.recentlyDeleted.deleteNowSubtitle"),
      cancelText: t("actions.deleteTranscriptCancelButton"),
      button: {
        text: t("settings.recentlyDeleted.deleteNow"),
        destructive: true,
        onPress: async () => {
          useUISlice.getState().setAlertModal(undefined);
          await run(id, "deleteForever");
        },
      },
    });
  };

  const erasedOn = (deletedAt?: string) =>
    new Date(
      Date.parse(deletedAt || "") + 7 * 24 * 60 * 60 * 1000,
    ).toLocaleDateString(i18n.language, { month: "short", day: "numeric" });

  return (
    <div>
      <SettingsButton
        actionId="settings.recentlyDeleted.toggle"
        text={t("settings.recentlyDeleted.title")}
        subtext={t("settings.recentlyDeleted.subtitle")}
        expanded={isOpen}
        controls="settings-recently-deleted"
        onClick={toggle}
        icon={<TrashIcon size={22} />}
      />
      {isOpen && (
        <div
          id="settings-recently-deleted"
          aria-busy={!items}
          className="grid gap-2 border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
        >
          {!items ? (
            <p className="px-1 text-[14px] text-[var(--text-secondary)]">
              {t("settings.recentlyDeleted.loading")}
            </p>
          ) : items.length === 0 ? (
            <p className="px-1 text-[14px] text-[var(--text-secondary)]">
              {t("settings.recentlyDeleted.empty")}
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                data-testid="settings-recently-deleted-item"
                className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--fill-1)] p-4"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-[15px] font-medium text-[var(--text-primary)]">
                    {(item.fileName || t("settings.recentlyDeleted.untitled")).replace(/\.[^/.]+$/, "")}
                  </span>
                  <span className="text-[13px] text-[var(--text-secondary)]">
                    {t("settings.recentlyDeleted.erasedOn", { date: erasedOn(item.deletedAt) })}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    data-action-id="settings.recentlyDeleted.restore"
                    disabled={busyId === item.id}
                    onClick={() => run(item.id, "restore")}
                    className="btn btn-glass min-h-[var(--tap-min)] px-4 text-[14px]"
                  >
                    {t("settings.recentlyDeleted.restore")}
                  </button>
                  <button
                    type="button"
                    data-action-id="settings.recentlyDeleted.deleteNow"
                    disabled={busyId === item.id}
                    onClick={() => confirmDeleteNow(item.id)}
                    aria-label={t("settings.recentlyDeleted.deleteNow")}
                    className="btn btn-quiet min-h-[var(--tap-min)] px-3 text-[var(--destructive)]"
                  >
                    <TrashIcon size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const SettingsPanel = ({ children }: { children: React.ReactNode }) => (
  <div className="glass-tile divide-y divide-[var(--hairline)] overflow-hidden rounded-[26px]">
    {children}
  </div>
);

const openDownload = (destination: string, url: string) => {
  logClientEvent("acquisition.downloadSelected", {
    destination,
    surface: "settings",
  });
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // External downloads are optional and must not break settings.
  }
};

const themeOptions: {
  value: ColorScheme;
  labelKey: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "system",
    labelKey: "settings.themeSystem",
    icon: <SystemIcon size={18} />,
  },
  {
    value: "light",
    labelKey: "settings.themeLight",
    icon: <SunIcon size={18} />,
  },
  {
    value: "dark",
    labelKey: "settings.themeDark",
    icon: <MoonIcon size={18} />,
  },
];

export default function Settings({
  onClose,
  isOpen = true,
}: {
  onClose?: () => void;
  // The home slide-over keeps this component mounted while hidden; the back
  // handler must only be attached while it is actually presented.
  isOpen?: boolean;
}) {
  const { t } = useTranslation();
  const desktopHost = useSharedDesktopHost();
  const userToken = useAppStore((state) => state.userToken);
  const userData = useAppStore((state) => state.userData);
  const isSubscribed = useAppStore((state) => state.isSubscribed);
  const hasTranscripts = useAppStore((state) => state.transcripts.length > 0);
  const dictationLanguage = userData?.preferences?.dictationLanguage;
  const subscriptionManagementProvider = useAppStore(
    (state) => state.subscriptionManagementProvider,
  );
  const isValidEmail = useAppStore((state) => {
    const user = state?.userData;
    if (!user) return false;
    // userData is a Partial<User> and is set optimistically (e.g. a language
    // change before user().get resolves), so email/id can be undefined —
    // guard both or this selector throws and white-screens settings.
    return !!user.email && !user.email.includes(user.id ?? "");
  });
  const router = useRouter();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isParagraphLengthOpen, setIsParagraphLengthOpen] = useState(false);
  const [webAnnualUpgrade, setWebAnnualUpgrade] = useState<SubscriptionOffer>();
  const [isVocabularyOpen, setIsVocabularyOpen] = useState(false);
  const [isDownloadsOpen, setIsDownloadsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showDictationOnboarding, setShowDictationOnboarding] = useState(false);
  const aiConsent = useAppStore((state) => state.userData?.aiConsent);
  const aiConsentBusy = useRef(false);
  const accountDeleteBusy = useRef(false);

  const manageAiConsent = () => {
    if (aiConsentBusy.current) return;
    const withdraw = useAppStore.getState().userData?.aiConsent?.version === 1;
    useUISlice.getState().setAlertModal({
      title: t(withdraw ? "accountPrivacy.withdrawTitle" : "accountPrivacy.aiTitle"),
      subtitle: t(withdraw ? "accountPrivacy.withdrawBody" : "accountPrivacy.aiBody"),
      privacyLink: !withdraw,
      cancelText: t("accountPrivacy.cancel"),
      button: {
        text: t(withdraw ? "accountPrivacy.withdraw" : "accountPrivacy.agree"),
        onPress: async () => {
          if (aiConsentBusy.current) return;
          aiConsentBusy.current = true;
          try {
            const result = await actions.user.setAiConsent(!withdraw);
            if ("error" in result) { toast.error(t("accountPrivacy.failed")); return; }
            useUISlice.getState().setAlertModal(undefined);
          } finally { aiConsentBusy.current = false; }
        },
      },
    });
  };

  const confirmAccountDeletion = () => {
    if (accountDeleteBusy.current) return;
    if (useUISlice.getState().liveRecording) {
      useUISlice.getState().setAlertModal({ title: t("accountPrivacy.busyTitle"), subtitle: t("accountPrivacy.busyBody") });
      return;
    }
    useUISlice.getState().setAlertModal({
      title: t("accountPrivacy.deleteTitle"),
      subtitle: t("accountPrivacy.deleteBody"),
      cancelText: t("accountPrivacy.cancel"),
      button: {
        text: t("accountPrivacy.deleteAccount"), destructive: true,
        onPress: async () => {
          if (accountDeleteBusy.current) return;
          accountDeleteBusy.current = true;
          const modal = useUISlice.getState().alertModal;
          if (modal) useUISlice.getState().setAlertModal({ ...modal, loading: true, hideCancel: true, hideDismissIcon: true, shouldPersistBackdropClick: true, onDismiss: () => {} });
          try {
            const result = await actions.user.delete();
            if ("error" in result) {
              if (modal) useUISlice.getState().setAlertModal(modal);
              toast.error(t("accountPrivacy.failed"));
              return;
            }
            await useAuth.signOut(undefined, () => router.replace("/"));
            useUISlice.getState().setAlertModal({
              title: t("accountPrivacy.deletedTitle"), subtitle: t("accountPrivacy.deletedBody"),
              button: { text: t("accountPrivacy.done"), onPress: () => useUISlice.getState().setAlertModal(undefined) },
            });
          } finally { accountDeleteBusy.current = false; }
        },
      },
    });
  };

  const [agentConnections, setAgentConnections] = useState<AgentConnectionSummary[]>([]);
  const [nativeDictationStatus, setNativeDictationStatus] =
    useState<GlobalDictationStatus>();
  const [dictationEnableBusy, setDictationEnableBusy] = useState(false);
  // Home's Dictate button opens this page as the Dictation panel: recent
  // dictations, then only the dictation settings. Read once per opening so
  // the slide-out animation keeps the mode it opened with.
  const [dictationOnly, setDictationOnly] = useState(false);
  const [recentDictations, setRecentDictations] = useState<
    WebDictationHistoryItem[]
  >([]);
  useLayoutEffect(() => {
    if (isOpen) setDictationOnly(window.location.hash === "#dictation-settings");
  }, [isOpen]);
  const settingsContentRef = useRef<HTMLElement | null>(null);
  const { colorScheme, setTheme } = useTheme();
  const isWeb = getPlatform() === "web";
  const hasGlobalDictation =
    getPlatform() === "android" || getPlatform() === "ios";
  const hasPlatformDictation = hasGlobalDictation;
  const nativeDictationSetupComplete = isGlobalDictationSetupComplete(
    nativeDictationStatus,
  );

  useEffect(() => {
    if (!isOpen || !isValidEmail) {
      setAgentConnections([]);
      return;
    }
    let active = true;
    void actions.agent.listConnections().then((result) => {
      if (active && "value" in result) setAgentConnections(result.value);
    });
    return () => {
      active = false;
    };
  }, [isOpen, isValidEmail, userToken]);

  const disconnectAgent = async (connectionId: string) => {
    const result = await actions.agent.disconnect(connectionId);
    if ("error" in result) {
      toast.error(t("agentSettings.disconnectFailed", { defaultValue: "Could not disconnect this assistant." }));
      return;
    }
    setAgentConnections((current) => current.filter((connection) => connection.id !== connectionId));
  };

  const emailComputerDownloads = () => {
    const recipient = accountEmail(userData);
    if (!recipient) {
      useAuth.signIn({ callback: () => {} });
      return;
    }
    logClientEvent("dictation.desktopCrossSellSelected", {
      source: "settings",
      destination: "directDownloads",
      method: "emailDraft",
    });
    try {
      openEmailDraft({
        to: recipient,
        subject: t("settings.computerDownloadEmailSubject", {
          defaultValue: "Install 1Transcribe on your computer",
        }),
        body: t("settings.computerDownloadEmailBody", {
          defaultValue:
            "Open this email on your computer and choose a download:\n\nWindows:\n{{windowsUrl}}\n\nmacOS:\n{{macUrl}}\n\nAfter installing, sign in to 1Transcribe with:\n{{accountEmail}}\n\nYour transcripts and subscription will stay connected.",
          windowsUrl: APP_DOWNLOAD_URLS.windows,
          macUrl: APP_DOWNLOAD_URLS.mac,
          accountEmail: recipient,
        }),
      });
    } catch {
      // A missing mail client must not disrupt Settings.
    }
  };

  const refreshNativeDictation = useCallback(async () => {
    if (!hasGlobalDictation) return;
    try {
      setNativeDictationStatus(
        await configureGlobalDictationAsync({
          authToken: userToken || "",
          serverUrl: config.apiUrl,
        }),
      );
    } catch (error) {
      console.error("[GlobalDictation] settings configure failed", error);
      setNativeDictationStatus(await getGlobalDictationStatusAsync());
    }
  }, [hasGlobalDictation, userToken]);

  useEffect(() => {
    if (!isOpen || !hasGlobalDictation) return;
    void refreshNativeDictation();
    let resumeListener: { remove: () => Promise<void> } | undefined;
    void runtime.app.onResume(refreshNativeDictation).then((listener) => {
      resumeListener = listener;
    });
    return () => {
      void resumeListener?.remove();
    };
  }, [hasGlobalDictation, isOpen, refreshNativeDictation]);

  const toggleNativeDictation = async () => {
    const nextEnabled = getNextEnabledValue(
      Boolean(nativeDictationStatus?.enabled),
      dictationEnableBusy,
    );
    if (nextEnabled === undefined) return;
    setDictationEnableBusy(true);
    try {
      setNativeDictationStatus(
        await setGlobalDictationEnabledAsync(nextEnabled),
      );
    } finally {
      setDictationEnableBusy(false);
    }
  };

  const toggleBubbleFaint = async () => {
    if (dictationEnableBusy) return;
    setDictationEnableBusy(true);
    try {
      setNativeDictationStatus(
        await setGlobalDictationBubbleFaintAsync(
          !nativeDictationStatus?.bubbleFaint,
        ),
      );
    } finally {
      setDictationEnableBusy(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !isWeb || !userToken) {
      setWebAnnualUpgrade(undefined);
      return;
    }
    let active = true;
    void actions.subscription.status();
    void actions.subscription.stripeAnnualUpgradeOffer().then((result) => {
      if (active) setWebAnnualUpgrade("value" in result ? result.value : undefined);
    });
    return () => {
      active = false;
    };
  }, [isOpen, isWeb, userToken]);

  useEffect(() => {
    if (!isOpen || !dictationOnly) return;
    if (isWeb) setRecentDictations(readWebDictationHistory(window.localStorage));
    else void getRecentGlobalDictationsAsync().then(setRecentDictations);
  }, [dictationOnly, isOpen, isWeb]);

  const copyRecentDictation = async (text: string) => {
    try {
      await runtime.clipboard.write(text);
      return true;
    } catch {
      return false;
    }
  };

  // A weekly native subscriber can move to the annual plan in place. Google
  // Play needs the old product to replace the subscription (handled in the
  // RevenueCat client); the App Store swaps within the subscription group.
  const nativePurchase = useAppStore((state) => state.nativePurchase);
  const nativeAnnualUpgrade = useMemo(() => {
    if (isWeb || !isSubscribed || !nativePurchase) return undefined;
    const weeklyProductId =
      nativePurchase.customerInfo.activeSubscriptions.find(
        (id) => getBillingPeriodFromIdentifier(id) === "weekly",
      );
    const annualPackage = nativePurchase.packages.find(
      (pkg) => getPackageBillingPeriod(pkg) === "annual",
    );
    if (!weeklyProductId || !annualPackage) return undefined;
    return { weeklyProductId, annualPackage };
  }, [isWeb, isSubscribed, nativePurchase]);

  const handleUpgradeToAnnual = async () => {
    if (webAnnualUpgrade) {
      logClientEvent("subscription.upgradeStarted", {
        package: webAnnualUpgrade.subscriptionPlan,
      });
      const result = await actions.subscription.stripeAnnualUpgrade();
      if ("error" in result) {
        toast.error(t("actions.subscriptionError"));
        return;
      }
      window.open(result.value, "_self");
      return;
    }
    if (!nativeAnnualUpgrade) return;
    logClientEvent("subscription.upgradeStarted", {
      package: nativeAnnualUpgrade.annualPackage.identifier,
    });
    const result = await actions.subscription.revenueCatPurchase(
      nativeAnnualUpgrade.annualPackage.identifier,
      { upgradeFromProductIdentifier: nativeAnnualUpgrade.weeklyProductId },
    );
    if ("error" in result) {
      if (result.error !== "purchaseCancelled") {
        toast.error(t("actions.subscriptionError"));
      }
      return;
    }
    toast.success(t("settings.upgradeToAnnualSuccess"));
  };

  const scrollRafRef = useRef<number | null>(null);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      setIsScrolled(scrollTop > 10);
      scrollRafRef.current = null;
    });
  }, []);

  const getCurrentLanguageLabel = () => {
    return (
      appLanguages.find((lang) => lang.value === i18n.language)?.label ||
      "English"
    );
  };

  const getCurrentThemeLabel = () => {
    const option = themeOptions.find((opt) => opt.value === colorScheme);
    return option ? t(option.labelKey) : t("settings.themeSystem");
  };

  const paragraphSentenceLimit = userData?.preferences?.paragraphSentenceLimit;
  const getParagraphLengthLabel = (
    value: ParagraphSentenceLimit | undefined,
  ) =>
    value === undefined
      ? t("settings.paragraphLengthAutomatic", {
          defaultValue: "Automatic",
        })
      : t("settings.paragraphLengthSentences", {
          count: value,
          defaultValue: "{{count}} sentences",
        });

  const selectParagraphLength = async (
    value: ParagraphSentenceLimit | undefined,
  ) => {
    const result = await actions.user.changeParagraphSentenceLimit(value);
    if ("error" in result) {
      toast.error(
        t("settings.paragraphLengthSaveError", {
          defaultValue: "Could not save paragraph length. Please try again.",
        }),
      );
      return;
    }
    setIsParagraphLengthOpen(false);
  };

  const openDictationLanguage = () => {
    useUISlice.getState().setAlertLanguagePicker({
      title: t("settings.dictationLanguage"),
      selectedLanguage: dictationLanguage,
      onSelect: (languageCode) => {
        void actions.user
          .changeDictationLanguage(languageCode)
          .then((result) => {
            if ("error" in result) {
              toast.error(t("settings.dictationLanguageSaveError"));
              return;
            }
            if (getPlatform() === "android") {
              void configureGlobalDictationAsync({
                authToken: userToken || "",
                serverUrl: config.apiUrl,
                dictationLanguage: languageCode,
              }).catch((error) => {
                console.error("[GlobalDictation] language sync failed", error);
              });
            }
          });
      },
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Android hardware back: close whatever is on top, otherwise close the
  // settings surface instead of letting the OS dismiss the app.
  useEffect(() => {
    if (!runtime.isNative || !isOpen) return;
    let disposed = false;
    let attached: { remove: () => Promise<void> } | undefined;
    void runtime.app
      .onBackButton(() => {
        // The dictation onboarding attaches its own back listener.
        if (showDictationOnboarding) return;
        const ui = useUISlice.getState();
        if (ui.alertPromptSignIn) {
          if (!ui.alertPromptSignIn.hideDismissIcon) {
            ui.alertPromptSignIn.onDismiss?.();
            ui.setAlertPromptSignIn(undefined);
          }
          return;
        }
        if (ui.alertModal) {
          if (!ui.alertModal.hideDismissIcon) {
            if (ui.alertModal.onDismiss) ui.alertModal.onDismiss();
            else ui.setAlertModal(undefined);
          }
          return;
        }
        if (ui.alertPrompt) {
          ui.setAlertPrompt(undefined);
          return;
        }
        if (showCancelForm) {
          setShowCancelForm(false);
          return;
        }
        if (isLangOpen) {
          setIsLangOpen(false);
          return;
        }
        if (isThemeOpen) {
          setIsThemeOpen(false);
          return;
        }
        if (isParagraphLengthOpen) {
          setIsParagraphLengthOpen(false);
          return;
        }
        if (isVocabularyOpen) {
          setIsVocabularyOpen(false);
          return;
        }
        if (isDownloadsOpen) {
          setIsDownloadsOpen(false);
          return;
        }
        if (onClose) onClose();
        else router.push("/home");
      })
      .then((listener) => {
        if (disposed) void listener.remove();
        else attached = listener;
      });

    return () => {
      disposed = true;
      void attached?.remove();
    };
  }, [
    isLangOpen,
    isDownloadsOpen,
    isOpen,
    isParagraphLengthOpen,
    isThemeOpen,
    isVocabularyOpen,
    onClose,
    router,
    showCancelForm,
    showDictationOnboarding,
  ]);

  // Authentication check - redirect if not authenticated. Only the
  // standalone web /settings route may do this: the home slide-over keeps
  // this component mounted at all times, and on native /auth immediately
  // bounces back to /home — redirecting from either produces an infinite
  // auth↔home loop whenever the session can't be refreshed (e.g. offline).
  useEffect(() => {
    if (onClose || runtime.isNative) return;
    if (
      !useAuth.isAuthenticated() &&
      router.query.checkout_success !== "true"
    ) {
      router.push("/auth");
    }
  }, [onClose, router, router.query.checkout_success]);

  const _copy = async (text: string) => {
    try {
      await runtime.clipboard.write(text);
      toast.success(t("settings.copied"), {
        style: {
          background: "rgba(17, 16, 34, 0.8)",
          color: "#fff",
          backdropFilter: "blur(10px)",
        },
      });
    } catch (error) {
      toast.error(t("settings.copyFailed"));
    }
  };

  const _selectLanguage = (langValue: string) => {
    void actions.user.update({ deviceLanguage: langValue });
    setTimeout(() => {
      router.replace({
        pathname: "/settings",
        search: `?${new URLSearchParams({ appLang: langValue })}`,
      });
      window.location.reload();
    }, 500);
  };

  return (
    <Container
      className={`${
        onClose ? "absolute inset-0" : "fixed inset-0"
      } bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden selection:bg-[var(--accent)]/20`}
    >
      {/* Background Ambience */}
      <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-[var(--ambience-blue)] rounded-full blur-[60px] opacity-60 pointer-events-none will-change-transform" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-[var(--ambience-purple)] rounded-full blur-[50px] opacity-60 pointer-events-none will-change-transform" />

      {/* Header */}
      <header
        className={`shrink-0 z-20 transition-all duration-300 ${
          isScrolled ? "glass-thin rounded-none" : "bg-transparent"
        }`}
      >
        <div className="px-5 md:px-8 py-4 flex items-center justify-between max-w-3xl mx-auto w-full">
          <button
            type="button"
            aria-label={t("globalDictationOnboarding.back")}
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                router.replace(`/home`);
              }
            }}
            data-action-id="settings.back"
            className="icon-btn w-12 h-12"
          >
            <ChevronLeftIcon size={26} />
          </button>
          <h1 className="text-[17px] font-semibold tracking-tight text-[var(--text-primary)]">
            {dictationOnly ? t("settings.dictationSection") : t("settings.title")}
          </h1>
          <div className="w-12" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Content */}
      <main
        ref={settingsContentRef}
        className="flex-1 px-5 md:px-8 py-6 space-y-8 relative z-10 overflow-y-auto pb-32 max-w-3xl mx-auto w-full"
        onScroll={handleScroll}
      >
        {dictationOnly &&
          // Only Android's bubble keeps recent results; the iOS keyboard types
          // straight into the other app, so its list would stay empty.
          (recentDictations.length > 0 || getPlatform() === "android") && (
            <DictationHistoryList
              history={recentDictations}
              onCopy={copyRecentDictation}
              empty={t("globalDictationOnboarding.intro.subtitle")}
            />
          )}

        {/* Account and plan actions stay together on every platform. */}
        <section hidden={dictationOnly} className="space-y-3">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.accountAndPlan", { defaultValue: "Account & plan" })}
          </h2>
          {!isValidEmail && (
            <div className="glass p-7 rounded-[28px] relative overflow-hidden group">
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[var(--fill-2)] flex items-center justify-center">
                  <CrownIcon size={32} className="text-[var(--text-primary)]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)] mb-1">
                    {t("settings.signInToSync")}
                  </h3>
                  <p className="text-sm text-[var(--text-secondary)] font-medium">
                    {t("settings.signInSubtitle")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    logClientEvent("settings.signInSelected");
                    useAuth.signIn({
                      callback: () => {
                        router.replace("/home");
                        window.location.reload();
                      },
                    });
                  }}
                  data-action-id="settings.signIn"
                  className="btn btn-primary mt-4 text-sm"
                >
                  {t("settings.signIn")}
                </button>
              </div>
            </div>
          )}
          {(isValidEmail || isSubscribed) && (
            <SettingsPanel>
              {isValidEmail && (
                <SettingsButton
                  actionId="settings.email.copy"
                  text={userData?.email || t("settings.signIn")}
                  subtext={t("settings.tapToCopyEmail")}
                  onClick={() => userData?.email && _copy(userData.email)}
                  icon={<CrownIcon size={22} />}
                />
              )}
              {isWeb && isSubscribed && subscriptionManagementProvider && (
                <SettingsButton
                  actionId="settings.subscription.manage"
                  text={t("settings.manageSubscription")}
                  onClick={async () => {
                    const result = await actions.subscription.manage();
                    if ("error" in result) {
                      toast.error(t("useAuth.signInFailed"));
                      return;
                    }
                    window.open(result.value, "_self");
                  }}
                  icon={<CardIcon size={22} />}
                />
              )}
              {(nativeAnnualUpgrade || webAnnualUpgrade) && (
                <SettingsButton
                  actionId="settings.subscription.upgradeAnnual"
                  text={t("settings.upgradeToAnnual")}
                  subtext={t("settings.upgradeToAnnualSubtitle", {
                    price: nativeAnnualUpgrade
                      ? nativeAnnualUpgrade.annualPackage.product.priceString
                      : new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: webAnnualUpgrade!.currency,
                        }).format(webAnnualUpgrade!.unitAmount / 100),
                  })}
                  onClick={handleUpgradeToAnnual}
                  icon={<CardIcon size={22} />}
                />
              )}
              {isSubscribed && (
                <SettingsButton
                  actionId="settings.subscription.cancel"
                  text={t("settings.cancelSubscription")}
                  subtext={t("settings.cancelSubscriptionSubtitle")}
                  onClick={() => setShowCancelForm(true)}
                  icon={<CloseIcon size={22} />}
                />
              )}
            </SettingsPanel>
          )}
        </section>

        {isValidEmail && agentConnections.length > 0 && (
          <section hidden={dictationOnly} className="space-y-3">
            <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
              {t("agentSettings.title", { defaultValue: "AI assistants" })}
            </h2>
            <p className="px-3 text-sm text-[var(--text-secondary)]">
              {t("agentSettings.subtitle", { defaultValue: "Connect once, then find and organize your 1Transcribe recordings from your assistant." })}
            </p>
            <SettingsPanel>
              {/* Hidden until MCP setup is ready.
              <SettingsButton
                actionId="settings.agent.copyMcpUrl"
                text={t("agentSettings.copyMcpUrl")}
                subtext={t("agentSettings.mcpHelp")}
                onClick={() => void _copy(new URL("/mcp", config.apiUrl).toString())}
                icon={<FileTextIcon size={22} />}
              />
              */}
              {agentConnections.map((connection) => (
                <SettingsButton
                  key={connection.id}
                  actionId="settings.agent.disconnect"
                  text={connection.assistant || t("agentSettings.connectedAssistant", { defaultValue: "Connected assistant" })}
                  subtext={t("agentSettings.disconnect", { defaultValue: "Disconnect" })}
                  onClick={() => void disconnectAgent(connection.id)}
                  icon={<CloseIcon size={22} />}
                  destructive
                />
              ))}
            </SettingsPanel>
          </section>
        )}

        {/* App presentation comes before task-specific transcript controls. */}
        <section hidden={dictationOnly} className="space-y-3">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.app")}
          </h2>
          <SettingsPanel>
            <div>
              <SettingsButton
                actionId="settings.theme.toggle"
                text={t("settings.appearance")}
                subtext={getCurrentThemeLabel()}
                expanded={isThemeOpen}
                controls="settings-theme-options"
                onClick={() => setIsThemeOpen(!isThemeOpen)}
                icon={<SparklesIcon size={22} />}
              />
              {isThemeOpen && (
                <div
                  id="settings-theme-options"
                  role="radiogroup"
                  aria-label={t("settings.appearance")}
                  className="grid gap-2 border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
                >
                  {themeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={colorScheme === option.value}
                      onClick={() => setTheme(option.value)}
                      data-action-id="settings.theme.select"
                      className={`w-full min-h-[var(--tap-min)] p-4 rounded-2xl flex items-center justify-between transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${
                        colorScheme === option.value
                          ? "bg-[var(--fill-2)] text-[var(--text-primary)]"
                          : "bg-[var(--fill-1)] text-[var(--text-secondary)] hover:bg-[var(--fill-2)]"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={
                            colorScheme === option.value
                              ? "text-[var(--text-primary)]"
                              : "text-[var(--text-tertiary)]"
                          }
                        >
                          {option.icon}
                        </span>
                        <span className="text-[15px] font-medium">
                          {t(option.labelKey)}
                        </span>
                      </div>
                      {colorScheme === option.value && (
                        <CheckIcon
                          aria-hidden="true"
                          size={18}
                          className="text-[var(--accent)]"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <SettingsButton
                actionId="settings.language.toggle"
                text={t("settings.appLanguage", { language: "" })}
                subtext={getCurrentLanguageLabel()}
                expanded={isLangOpen}
                controls="settings-language-options"
                onClick={() => setIsLangOpen(!isLangOpen)}
                icon={<TranslateIcon size={22} />}
              />
              {isLangOpen && (
                <div
                  id="settings-language-options"
                  role="radiogroup"
                  aria-label={t("settings.appLanguage", { language: "" })}
                  className="grid gap-2 border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
                >
                  {appLanguages.map((lang) => (
                    <button
                      key={lang.value}
                      type="button"
                      role="radio"
                      aria-checked={i18n.language === lang.value}
                      onClick={() => _selectLanguage(lang.value)}
                      data-action-id="settings.language.select"
                      className={`w-full min-h-[var(--tap-min)] p-4 rounded-2xl flex items-center justify-between transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${
                        i18n.language === lang.value
                          ? "bg-[var(--fill-2)] text-[var(--text-primary)]"
                          : "bg-[var(--fill-1)] text-[var(--text-secondary)] hover:bg-[var(--fill-2)]"
                      }`}
                    >
                      <span className="text-[15px] font-medium ml-1">
                        {lang.label}
                      </span>
                      {i18n.language === lang.value && (
                        <CheckIcon
                          aria-hidden="true"
                          size={18}
                          className="text-[var(--accent)]"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SettingsPanel>
        </section>

        {/* Transcript-output preferences never mix with app appearance. */}
        <section hidden={dictationOnly} className="space-y-3">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.transcription", { defaultValue: "Transcription" })}
          </h2>
          <SettingsPanel>
            <div>
              <SettingsButton
                actionId="settings.paragraphLength.toggle"
                text={t("settings.paragraphLength", {
                  defaultValue: "Paragraph length",
                })}
                subtext={getParagraphLengthLabel(paragraphSentenceLimit)}
                expanded={isParagraphLengthOpen}
                controls="settings-paragraph-options"
                onClick={() => setIsParagraphLengthOpen(!isParagraphLengthOpen)}
                icon={<FileTextIcon size={22} />}
              />
              {isParagraphLengthOpen && (
                <div
                  id="settings-paragraph-options"
                  role="radiogroup"
                  aria-label={t("settings.paragraphLength", {
                    defaultValue: "Paragraph length",
                  })}
                  className="grid gap-2 border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
                >
                  <p className="px-2 pb-1 text-[13px] font-medium text-[var(--text-secondary)]">
                    {t("settings.paragraphLengthSubtitle", {
                      defaultValue:
                        "For new audio and video transcripts. Speaker changes always start a new paragraph.",
                    })}
                  </p>
                  {([undefined, ...PARAGRAPH_SENTENCE_LIMITS] as const).map(
                    (option) => (
                      <button
                        key={option ?? "automatic"}
                        type="button"
                        role="radio"
                        aria-checked={paragraphSentenceLimit === option}
                        onClick={() => void selectParagraphLength(option)}
                        data-action-id="settings.paragraphLength.select"
                        className={`w-full min-h-[var(--tap-min)] p-4 rounded-2xl flex items-center justify-between transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${
                          paragraphSentenceLimit === option
                            ? "bg-[var(--fill-2)] text-[var(--text-primary)]"
                            : "bg-[var(--fill-1)] text-[var(--text-secondary)] hover:bg-[var(--fill-2)]"
                        }`}
                      >
                        <span className="text-[15px] font-medium ml-1">
                          {getParagraphLengthLabel(option)}
                        </span>
                        {paragraphSentenceLimit === option && (
                          <CheckIcon
                            aria-hidden="true"
                            size={18}
                            className="text-[var(--accent)]"
                          />
                        )}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
            <RecentlyDeletedRow />
          </SettingsPanel>
        </section>

        {/* Dictation has shared vocabulary plus available platform setup. */}
        {!desktop.isAvailable && (
          <section id="dictation-settings" className="space-y-3">
            <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
              {t("settings.dictationSection")}
            </h2>
            <SettingsPanel>
              {hasPlatformDictation && (
                <SettingsButton
                  actionId="settings.dictation.setup"
                  text={t("settings.setUpDictation")}
                  onClick={() => setShowDictationOnboarding(true)}
                  subtext={
                    nativeDictationSetupComplete
                      ? getPlatform() === "ios" ||
                        nativeDictationStatus?.enabled
                        ? t("globalDictationBubble.statusActiveTitle")
                        : t("globalDictationBubble.statusPausedTitle")
                      : t("settings.setUpDictationSubtitle")
                  }
                  icon={<MicrophoneIcon size={22} />}
                />
              )}
              {getPlatform() === "android" && nativeDictationSetupComplete && (
                <div className="flex min-h-[76px] items-center justify-between gap-4 px-5 py-4">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)] text-[var(--text-primary)]">
                      <MicrophoneIcon size={22} />
                    </div>
                    <span className="text-[16px] font-semibold tracking-tight text-[var(--text-primary)]">
                      {t("globalDictationBubble.toggleLabel")}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(nativeDictationStatus?.enabled)}
                    data-action-id="dictation.enabled.toggle"
                    disabled={dictationEnableBusy}
                    onClick={() => void toggleNativeDictation()}
                    className={`flex h-8 w-13 shrink-0 items-center rounded-full p-1 transition-colors disabled:opacity-50 ${
                      nativeDictationStatus?.enabled
                        ? "justify-end bg-[var(--accent)]"
                        : "justify-start bg-[var(--fill-2)]"
                    }`}
                  >
                    <span className="switch-thumb h-6 w-6 rounded-full shadow" />
                  </button>
                </div>
              )}
              {getPlatform() === "android" &&
                nativeDictationSetupComplete &&
                nativeDictationStatus?.enabled && (
                  <div className="flex min-h-[76px] items-center justify-between gap-4 px-5 py-4">
                    <div className="flex min-w-0 items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
                        <span className="desktop-lesson-bubble" data-mode="idle" />
                      </div>
                      <div className="min-w-0">
                        <span className="block text-[16px] font-semibold tracking-tight text-[var(--text-primary)]">
                          {t("globalDictationBubble.faintLabel")}
                        </span>
                        <span className="block text-sm text-[var(--text-secondary)]">
                          {t("globalDictationBubble.faintSubtitle")}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(nativeDictationStatus?.bubbleFaint)}
                      aria-label={t("globalDictationBubble.faintLabel")}
                      data-action-id="dictation.bubble.faint"
                      disabled={dictationEnableBusy}
                      onClick={() => void toggleBubbleFaint()}
                      className={`flex h-8 w-13 shrink-0 items-center rounded-full p-1 transition-colors disabled:opacity-50 ${
                        nativeDictationStatus?.bubbleFaint
                          ? "justify-end bg-[var(--accent)]"
                          : "justify-start bg-[var(--fill-2)]"
                      }`}
                    >
                      <span className="switch-thumb h-6 w-6 rounded-full shadow" />
                    </button>
                  </div>
                )}
              <SettingsButton
                actionId="settings.dictationLanguage.open"
                text={t("settings.dictationLanguage")}
                subtext={
                  LANGUAGES.find(
                    (language) => language.value === dictationLanguage,
                  )?.label ||
                  t("alertLanguagePicker.title", {
                    defaultValue: "Select language",
                  })
                }
                onClick={openDictationLanguage}
                icon={<TranslateIcon size={22} />}
              />
              <div>
                <SettingsButton
                  actionId="settings.dictationVocabulary.toggle"
                  text={t("settings.dictationVocabulary")}
                  subtext={t("settings.dictationVocabularySubtitle")}
                  expanded={isVocabularyOpen}
                  controls="settings-vocabulary-editor"
                  onClick={() => setIsVocabularyOpen(!isVocabularyOpen)}
                  icon={<SparklesIcon size={22} />}
                />
                {isVocabularyOpen && (
                  <div
                    id="settings-vocabulary-editor"
                    className="border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
                  >
                    <DictationVocabularyEditor />
                  </div>
                )}
              </div>
            </SettingsPanel>
          </section>
        )}

        {!desktop.isAvailable && (
          <section hidden={dictationOnly} className="space-y-3">
            <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
              {t("settings.downloads", { defaultValue: "Downloads" })}
            </h2>
            <SettingsPanel>
              {isWeb ? (
                <div>
                  <SettingsButton
                    actionId="settings.downloads.toggle"
                    text={t("onboarding.planComparison.crossPlatformTitle")}
                    subtext={t("settings.dictationOnComputer", {
                      defaultValue: "Dictation on Mac & Windows",
                    })}
                    expanded={isDownloadsOpen}
                    controls="settings-download-options"
                    onClick={() => setIsDownloadsOpen(!isDownloadsOpen)}
                    icon={<DownloadIcon size={22} />}
                  />
                  {isDownloadsOpen && (
                    <div
                      id="settings-download-options"
                      className="grid gap-2 border-t border-[var(--hairline)] bg-[var(--fill-1)]/40 px-4 py-4 animate-in slide-in-from-top-2 fade-in duration-200"
                    >
                      <SettingsButton
                        actionId="settings.download.ios"
                        text={t("settings.downloadIos", {
                          defaultValue: "Download for iPhone & iPad",
                        })}
                        onClick={() => openDownload("ios", IOS_APP_LINK)}
                        icon={<AppleIcon size={22} />}
                      />
                      <SettingsButton
                        actionId="settings.download.android"
                        text={t("settings.downloadAndroid", {
                          defaultValue: "Download for Android",
                        })}
                        onClick={() =>
                          openDownload("android", ANDROID_APP_LINK)
                        }
                        icon={<AndroidIcon size={22} />}
                      />
                      <SettingsButton
                        actionId="settings.download.windows"
                        text={t("settings.downloadWindows", {
                          defaultValue: "Download for Windows",
                        })}
                        onClick={() =>
                          openDownload(
                            "windowsDesktop",
                            APP_DOWNLOAD_URLS.windows,
                          )
                        }
                        icon={<WindowsIcon size={22} />}
                      />
                      <SettingsButton
                        actionId="settings.download.mac"
                        text={t("settings.downloadMac", {
                          defaultValue: "Download for macOS",
                        })}
                        onClick={() =>
                          openDownload("macDesktop", APP_DOWNLOAD_URLS.mac)
                        }
                        icon={<AppleIcon size={22} />}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <SettingsButton
                  actionId="settings.download.apps"
                  text={t("settings.useOnComputer", {
                    defaultValue: "Use on your computer",
                  })}
                  subtext={t("settings.dictationOnComputer", {
                    defaultValue: "Dictation on Mac & Windows",
                  })}
                  onClick={emailComputerDownloads}
                  icon={<DownloadIcon size={22} />}
                  singleLine
                />
              )}
            </SettingsPanel>
          </section>
        )}

        <section hidden={dictationOnly} className="space-y-3">
          <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest px-1 ml-2">
            {t("settings.supportAndLegal")}
          </h2>
          <SettingsPanel>
            <SettingsButton
              actionId="settings.support.contact"
              text={t("settings.contactSupport")}
              onClick={() => {
                contactUs(
                  encodeURIComponent(
                    `Hello I need help with 1Transcribe (#DO NOT REMOVE: ${useAuth?.currentAuthUser()?.userId})`,
                  ),
                );
              }}
              icon={<LightbulbIcon size={22} />}
            />
            <SettingsButton
              actionId="settings.share"
              text={t("settings.shareWithFriend")}
              onClick={() => {
                logClientEvent("settings.shareSelected");
                shareApp();
              }}
              icon={<ShareIcon size={22} />}
            />
            {runtime.platform === "ios" && (
              <SettingsButton
                actionId="settings.aiConsent"
                text={t("accountPrivacy.aiSettings")}
                subtext={t(aiConsent?.version === 1 ? "accountPrivacy.aiAllowed" : "accountPrivacy.aiNotAllowed")}
                onClick={manageAiConsent}
                icon={<SparklesIcon size={22} />}
              />
            )}
            <SettingsButton
              actionId="settings.privacy.open"
              text={t("settings.privacyPolicy")}
              onClick={gotoPrivacy}
              icon={<LegalIcon size={22} />}
            />
            <SettingsButton
              actionId="settings.terms.open"
              text={t("settings.termsOfService")}
              onClick={gotoTerms}
              icon={<LegalIcon size={22} />}
            />
          </SettingsPanel>
        </section>

        {desktop.isAvailable && (
          <section
            data-testid="desktop-update-settings-section"
            aria-label={t("desktopDictation.checkUpdate", {
              defaultValue: "Check for desktop update",
            })}
            className="pt-2"
          >
            <DesktopDictationSettings host={desktopHost} mode="app" />
          </section>
        )}

        {isValidEmail && (
          <section
            hidden={dictationOnly}
            aria-label={t("settings.logout")}
            className="pt-2"
          >
            <SettingsPanel>
              <SettingsButton
                actionId="settings.signOut"
                text={t("settings.logout")}
                onClick={() => useAuth.signOut()}
                destructive
                icon={<CloseIcon size={22} />}
              />
            </SettingsPanel>
          </section>
        )}
        {(isValidEmail || hasTranscripts) && (
          <section hidden={dictationOnly} className="pt-2">
            <SettingsPanel>
              <SettingsButton
                actionId="settings.deleteAccount"
                text={t("accountPrivacy.deleteAccount")}
                onClick={confirmAccountDeletion}
                destructive
                icon={<TrashIcon size={22} />}
              />
            </SettingsPanel>
          </section>
        )}
      </main>
      {showCancelForm && (
        <CancelSubscriptionForm onClose={() => setShowCancelForm(false)} />
      )}
      {hasPlatformDictation && (
        <GlobalDictationOnboarding
          open={showDictationOnboarding}
          onClose={() => {
            setShowDictationOnboarding(false);
            void refreshNativeDictation();
          }}
        />
      )}
    </Container>
  );
}
